import type {
  ColumnDef,
  OnChangeFn,
  RowData,
  SortingState,
} from '@tanstack/react-table';
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '~/components/Skeleton';
import { cn } from '~/lib/cn';

import { TableHead } from './components/TableHead';
import { TableRow } from './components/TableRow';

export interface TableProps<TData>
  extends React.HTMLAttributes<HTMLTableElement> {
  data: TData[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];

  // rely on server-side sorting
  // if true, the table will not handle sorting on the client side
  manualSorting?: boolean;

  // Table specific props
  className?: string;

  // Row specific props
  rowClassName?: string;
  onRowClick?: (row: TData) => void;

  // Cell specific props
  cellClassName?: string;

  // Sorting specific props
  sort?: {
    onSortingChange: OnChangeFn<SortingState>;
    sorting: SortingState;
  };

  skeletonLoading?: {
    isLoading: boolean;
    prefillRowsNumber?: number;
  };

  containerClassName?: string;
}

export function Table<TData extends RowData>({
  data,
  columns,
  manualSorting = true,
  className,
  rowClassName,
  cellClassName,
  onRowClick,
  sort,
  skeletonLoading,
  containerClassName,
  ...tableProps
}: TableProps<TData>) {
  const { t } = useTranslation('components', { keyPrefix: 'table' });
  const { onSortingChange, sorting } = sort ?? {};

  const { isLoading = false, prefillRowsNumber = 10 } = skeletonLoading ?? {};

  const tableData = useMemo(
    () => (isLoading ? Array(prefillRowsNumber).fill({}) : data),
    [isLoading, prefillRowsNumber, data],
  );

  const columnsMemo = useMemo(
    () =>
      isLoading
        ? columns.map((column) => ({ ...column, cell: () => <Skeleton /> }))
        : columns,
    [isLoading, columns],
  );

  const table = useReactTable({
    data: tableData,
    columns: columnsMemo,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting,
    onSortingChange,
    state: {
      sorting,
    },
    // This prevents invalid handling of sort direction for columns with incomplete data
    // See more: https://github.com/TanStack/table/issues/4289
    sortDescFirst: true,
    // Enable sorting only if onSortingChange is provided
    enableSorting: !!onSortingChange,
  });

  /**
   * Memoization of column size variables
   * This is done to ensure that the column sizes are recalculated only when necessary,
   *
   * https://tanstack.com/table/v8/docs/framework/react/examples/column-resizing-performant
   */

  const { columnSizingInfo, columnSizing } = table.getState();
  const columnSizeVars = useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number } = {};

    for (const header of headers) {
      colSizes[`--header-${header.id}-size`] = header.getSize();
      colSizes[`--col-${header.column.id}-size`] = header.column.getSize();
    }

    return colSizes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSizingInfo, columnSizing, columns]);

  return (
    <div className={cn('w-full overflow-x-auto', containerClassName)}>
      <table
        {...tableProps}
        className={cn(
          'w-full table-fixed border-separate border-spacing-0 pt-lg',
          className,
        )}
        style={{ ...tableProps.style, ...columnSizeVars }}
      >
        <TableHead headerGroups={table.getHeaderGroups()} />
        <tbody className="text-body-md">
          {data.length || isLoading ? (
            table
              .getRowModel()
              .rows.map((row) => (
                <TableRow
                  key={row.id}
                  row={row}
                  className={rowClassName}
                  cellClassName={cellClassName}
                  onClick={
                    !isLoading && onRowClick
                      ? () => onRowClick?.(row.original)
                      : undefined
                  }
                />
              ))
          ) : (
            <tr>
              <td colSpan={columns.length} className="p-md">
                {t('noData')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

Table.displayName = 'Table';
